import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { dayLabel } from "./data";

const sel = "w-full border border-input bg-white h-10 px-3 text-sm";
const Field = ({ label, children }) => <label className="block text-xs text-[#575757]">{label}<div className="mt-1">{children}</div></label>;

const TicketOption = ({ t, active, onPick }) => (
  <button type="button" onClick={() => onPick(t.key)} data-testid={`event-ticket-${t.key}`}
    className={`text-left p-4 border transition-colors ${active ? "border-[#E5332A] bg-[#FFF5F4]" : "border-[#E7E7E7] hover:border-[#0A0A0A]"}`}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-bold text-sm">{t.name}</span>
      <span className="font-display font-bold whitespace-nowrap">£{t.price.toLocaleString("en-GB")}</span>
    </div>
    <p className="text-[11px] text-[#575757] mt-1">{t.unit} · {t.includes}</p>
  </button>
);

export const Register = ({ data, session, setSession }) => {
  const upcoming = data.sessions.filter((s) => s.status === "upcoming");
  const open = upcoming.filter((s) => s.seats_left > 0);
  const q = new URLSearchParams(window.location.search);
  const [ticket, setTicket] = useState(data.tickets.some((x) => x.key === q.get("ticket")) ? q.get("ticket") : "oc_individual");
  const [f, setF] = useState({ category: "", name: q.get("name") || "", email: q.get("email") || "", company: q.get("company") || "", job_title: "", phone: "", guest_names: "", dietary: "", newsletter_optin: true, share_in_list: false });
  const [busy, setBusy] = useState(false);
  const t = data.tickets.find((x) => x.key === ticket);
  const passBlocked = t.pass ? upcoming.filter((s) => s.seats_left < t.seats) : [];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Choose your participation category");
    if (!t.pass && !session) return toast.error("Choose a session");
    setBusy(true);
    try {
      const { data: r } = await api.post("/events/register", { ...f, ticket, session_date: t.pass ? null : session, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  return (
    <section id="register" className="bg-[#F4F4F4]" data-testid="events-register">
      <div className="max-w-7xl mx-auto px-5 py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="overline text-[#E5332A]">Register</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">Reserve your seat at The Origin Cup</h2>
          <p className="text-sm text-[#575757] mt-4 leading-relaxed">Pick your ticket, your session and your participation category, then pay securely by card. Every session is strictly capped at 20 guests, so early booking is advised.</p>
          <div className="grid gap-3 mt-8">{data.tickets.map((x) => <TicketOption key={x.key} t={x} active={x.key === ticket} onPick={setTicket} />)}</div>
          <p className="text-[11px] text-[#575757] mt-3">All prices exclude VAT. Group and multi-session discounts on request: admin@africabrewbrief.com</p>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[#E7E7E7] p-6 sm:p-8 space-y-4 self-start" data-testid="event-register-form">
          {t.pass ? (passBlocked.length ? (
            <p className="text-sm bg-[#FFF5F4] border border-[#E5332A] text-[#0A0A0A] p-4" data-testid="event-pass-soldout">Season passes are paused because {passBlocked.map((s) => s.country).join(", ")} {passBlocked.length > 1 ? "are" : "is"} full. Book individual sessions instead, or email admin@africabrewbrief.com.</p>
          ) : (
            <p className="text-sm bg-[#0A0A0A] text-white p-4" data-testid="event-pass-note">Your Season Pass covers every remaining Year One session ({upcoming.length}): {upcoming.map((s) => s.country).join(", ")}.</p>
          )) : (
            <Field label="Session">
              <select data-testid="event-register-session" value={session} onChange={(e) => setSession(e.target.value)} className={sel}>
                <option value="">Choose a session</option>
                {open.map((s) => <option key={s.date} value={s.date}>{dayLabel(s.date)} · {s.country} · {s.seats_left} seats left</option>)}
              </select>
            </Field>
          )}
          <Field label="Participation category">
            <select data-testid="event-register-category" value={f.category} onChange={set("category")} className={sel}>
              <option value="">Choose the one that fits you best</option>
              {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name"><Input data-testid="event-register-name" required value={f.name} onChange={set("name")} className="rounded-none" /></Field>
            <Field label="Email"><Input data-testid="event-register-email" type="email" required value={f.email} onChange={set("email")} className="rounded-none" /></Field>
            <Field label="Organisation"><Input data-testid="event-register-company" value={f.company} onChange={set("company")} className="rounded-none" /></Field>
            <Field label="Job title"><Input data-testid="event-register-job" value={f.job_title} onChange={set("job_title")} className="rounded-none" /></Field>
            <Field label="Phone (optional)"><Input data-testid="event-register-phone" value={f.phone} onChange={set("phone")} className="rounded-none" /></Field>
            <Field label="Dietary needs (optional)"><Input data-testid="event-register-dietary" value={f.dietary} onChange={set("dietary")} className="rounded-none" /></Field>
          </div>
          {t.seats > 1 && <Field label="Names of your other guests (optional, you can send them later)"><Input data-testid="event-register-guests" value={f.guest_names} onChange={set("guest_names")} className="rounded-none" /></Field>}
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="event-register-newsletter" checked={f.newsletter_optin} onChange={set("newsletter_optin")} className="mt-1 accent-[#E5332A]" />
            <span>Also send me <b>The Tuesday Roast</b>, Africa Brew Brief's free weekly markets and deals newsletter.</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="event-register-share-list" checked={f.share_in_list} onChange={set("share_in_list")} className="mt-1 accent-[#E5332A]" />
            <span>List my name, role and organisation in the attendee list we email to fellow guests after the session. Your email and phone are never shared.</span>
          </label>
          <p className="text-[11px] text-[#575757]">By booking you join the Origin Cup guest list and receive monthly session details about two weeks before each event. You can opt out from any email.</p>
          <button disabled={busy || passBlocked.length > 0} data-testid="event-register-submit" className="w-full bg-[#E5332A] hover:bg-[#B8241C] text-white h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {busy && <Loader2 size={16} className="animate-spin" />} Continue to payment · £{t.price.toLocaleString("en-GB")} + VAT
          </button>
        </form>
      </div>
    </section>
  );
};
